import React, { type ReactNode } from 'react';
import { StyleSheet } from 'react-native';
import { View } from './rn';
import { useTheme } from '../theme/ThemeContext';
import { profilePalette } from '../theme/profileSection';
import { radius } from '../theme';

// Only titled groups need a tinted surround. Standalone lists have one frame.
export default function ProfileSection({ header, children, testID, separated }: {
  header?: ReactNode;
  children: ReactNode;
  testID?: string;
  /**
   * Satır aralarına ince ayraç koyar (7.14 · D-545).
   *
   * İstenerek açılıyor, varsayılan değil: bölümlerin hepsi satır listesi değil.
   * Gizlilik ve Görünüm bölümleri tik satırı, ipucu metni ve çip grubu
   * karışımı taşıyor; oralara körlemesine çizgi koymak bir ipucunu ait olduğu
   * etiketten ayırırdı. Ayraç okumayı kolaylaştırdığı yerde açılır.
   */
  separated?: boolean;
}) {
  const colors = useTheme();
  const hasHeader = Boolean(header);
  return (
    <View testID={testID ? `${testID}-frame` : 'profile-section-frame'} style={[styles.frame, !hasHeader && styles.standaloneFrame, {
      borderColor: colors.gray200,
      backgroundColor: hasHeader ? profilePalette(colors).surround : colors.white,
    }]}>
      {header && <View testID={testID ? `${testID}-header` : 'profile-section-header'} style={styles.header}>{header}</View>}
      <View testID={testID ? `${testID}-body` : 'profile-section-body'} style={[styles.body, !hasHeader && styles.standaloneBody, {
        borderColor: colors.gray200, backgroundColor: colors.white,
      }]}>
        {separated ? withSeparators(children, colors.gray200) : children}
      </View>
    </View>
  );
}

/**
 * Çocukların ARASINA ayraç koyar, sonuna değil.
 *
 * `React.Children.toArray` koşullu çocukların `false` değerlerini de eler, yani
 * çizilmeyen bir satır için ayraç kalmaz — listenin sonunda boşlukta duran bir
 * çizgi oluşmaz.
 */
function withSeparators(children: ReactNode, color: string) {
  const items = React.Children.toArray(children);
  return items.map((child, index) => (
    <React.Fragment key={index}>
      {index > 0 && <View style={[styles.separator, { backgroundColor: color }]} />}
      {child}
    </React.Fragment>
  ));
}

const styles = StyleSheet.create({
  frame: {
    marginHorizontal: 16, marginTop: 16, padding: 6, minWidth: 0,
    borderWidth: 1, borderRadius: radius.layerOuter,
  },
  // Match the body's 1px border + 10px inset without growing action headers.
  header: { minHeight: 44, paddingHorizontal: 11, paddingVertical: 0, justifyContent: 'center' },
  body: { padding: 10, minWidth: 0, borderWidth: 1, borderRadius: radius.layerInner },
  standaloneFrame: { padding: 16 },
  standaloneBody: { padding: 0, borderWidth: 0, borderRadius: radius.square },
  separator: { height: StyleSheet.hairlineWidth },
});
